import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  BarChart3, 
  Plus, 
  Trash2,
  Vote
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/components/toast";
import { getPolls, createPoll, votePoll, deletePoll } from "@/lib/marketing-api";

export default function Polls() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    options: [{ text: "", votes: 0 }, { text: "", votes: 0 }]
  });

  useEffect(() => {
    loadPolls();
  }, []);

  const loadPolls = async () => {
    try {
      const response = await getPolls();
      setPolls(response.data?.data || response.data || []);
    } catch (error) {
      toast.error(t('errors.serverError'));
      setPolls([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const validOptions = formData.options.filter(o => o.text.trim());
    if (validOptions.length < 2) {
      toast.error(t('marketingFull.minTwoOptions'));
      return;
    }
    try {
      await createPoll({
        ...formData,
        options: validOptions
      });
      toast.success(t('marketingFull.pollCreated'));
      setIsDialogOpen(false);
      setFormData({
        title: "",
        description: "",
        options: [{ text: "", votes: 0 }, { text: "", votes: 0 }]
      });
      loadPolls();
    } catch (error) {
      toast.error(t('marketingFull.errorCreatingPoll'));
    }
  };

  const handleVote = async (pollId, optionIndex) => {
    try {
      await votePoll(pollId, optionIndex);
      toast.success(t('marketingFull.voteRecorded'));
      loadPolls();
    } catch (error) {
      toast.error(t('marketingFull.errorVoting'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('marketingFull.confirmDeletePoll'))) return;
    try {
      await deletePoll(id);
      toast.success(t('marketingFull.pollDeleted'));
      loadPolls();
    } catch (error) {
      toast.error(t('marketingFull.errorDeletingPoll'));
    }
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { text: "", votes: 0 }]
    });
  };

  const updateOption = (index, text) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], text };
    setFormData({ ...formData, options: newOptions });
  };

  const removeOption = (index) => {
    if (formData.options.length <= 2) return;
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="polls-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('marketingFull.pollsTitle')}</h1>
          <p className="text-muted-foreground mt-1">{t('marketingFull.pollsSubtitle')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-6 py-2 shadow-lg shadow-emerald-500/30 text-base" size="lg" data-testid="create-poll-btn">
              <Plus className="w-5 h-5 me-2" />
              + {t('marketingFull.newPoll')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" dir={document.documentElement.dir}>
            <DialogHeader>
              <DialogTitle>{t('marketingFull.createPoll')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="form-group">
                <Label htmlFor="title">{t('marketingFull.pollQuestion')}</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('marketingFull.pollQuestion')}
                  required
                  data-testid="poll-title-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="description">{t('marketingFull.pollDescriptionOptional')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('marketingFull.pollDescPlaceholder')}
                  data-testid="poll-description-input"
                />
              </div>
              <div className="form-group">
                <Label>{t('marketingFull.pollOptions')}</Label>
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={option.text}
                        onChange={(e) => updateOption(index, e.target.value)}
                        placeholder={`${t('marketingFull.optionPlaceholder')} ${index + 1}`}
                        data-testid={`poll-option-${index}`}
                      />
                      {formData.options.length > 2 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeOption(index)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={addOption}
                  data-testid="add-option-btn"
                >
                  <Plus className="w-4 h-4 me-1" />
                  {t('marketingFull.addOption')}
                </Button>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" data-testid="submit-poll-btn">
                  {t('marketingFull.createPoll')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Polls Grid */}
      {polls.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="empty-state">
              <BarChart3 className="empty-state-icon" />
              <h3 className="empty-state-title">{t('marketingFull.noPolls')}</h3>
              <p className="empty-state-description">{t('marketingFull.createFirstPoll')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {polls.map((poll) => (
            <Card key={poll.id} className="card-hover" data-testid={`poll-card-${poll.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{poll.title}</CardTitle>
                    {poll.description && (
                      <p className="text-sm text-muted-foreground mt-1">{poll.description}</p>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-destructive"
                    onClick={() => handleDelete(poll.id)}
                    data-testid={`delete-poll-${poll.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {poll.options.map((option, index) => {
                    const percentage = poll.total_votes > 0 
                      ? Math.round((option.votes / poll.total_votes) * 100) 
                      : 0;
                    return (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm">{option.text}</span>
                          <span className="text-sm font-numbers text-muted-foreground">
                            {option.votes} {t('marketingFull.vote')} ({percentage}%)
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={percentage} className="flex-1" />
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleVote(poll.id, index)}
                            data-testid={`vote-${poll.id}-${index}`}
                          >
                            <Vote className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
                  إجمالي الأصوات: <span className="font-numbers font-bold">{poll.total_votes}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
